/* eslint-disable @typescript-eslint/no-unused-vars */
import { fromEnumAsString, isNullOrUndefinedOrEmpty } from 'in-time-core';
import { logError, logInfo } from 'in-time-logger';
import { Renderer2 } from '@angular/core';
import { AnalyticsTracker } from './analytics-tracker';
import {
  AnalyticsTrackerType, CartEventArgs, ContentViewEventArgs, ExploreEventArgs, PageViewEventArgs,
  PromoCodeEventArgs, SearchEventArgs, SignUpEventArgs
} from './analytics.types';
import {
  GTM_ADD_TO_CART, GTM_COMPLETE_REGISTRATION, GTM_EXPLORE, GTM_INITIATE_CHECKOUT, GTM_INITIATE_PURCHASE,
  GTM_PROMO_CODE_ENTERED, GTM_PURCHASE, GTM_PURCHASE_CANCELED,
  GTM_PURCHASE_FAILED, GTM_SEARCH,
} from './analytics.events';
import { GtmHelper, TagManagerEntry } from '../helpers/gtm-helpers';

export class GtmTracker extends AnalyticsTracker {
  private readonly initializedTagManager: TagManagerEntry[] = [];
  private readonly customTagManagerStack: TagManagerEntry[] = [];
  private hasAcceptedCookies: boolean = false;
  private hasBeenInitialized: boolean = false;

  public readonly $typeId: AnalyticsTrackerType = 'gtm';

  static create(opts: {
    renderer: Renderer2,
    document: Document,
  }): GtmTracker {
    logInfo('Initialize GTM');
    return new GtmTracker(opts.renderer, opts.document);
  }

  private constructor(
    private readonly renderer: Renderer2,
    private readonly document: Document,
  ) { super(); }

  onInit(hasAcceptedCookies: boolean): void {
    this.hasAcceptedCookies = hasAcceptedCookies;
    this.hasBeenInitialized = true;
  }

  onDestroy(): void {
    // nothing to do here :)
  }

  onCookiesAccepted(): void {
    this.hasAcceptedCookies = true;
    this.trackConsent(this.hasAcceptedCookies);
  }

  onCookiesDeclined(): void {
    this.hasAcceptedCookies = false;
    this.trackConsent(this.hasAcceptedCookies);
  }

  activateCustomTagManager(opts: { context: string, customTagManagerId: string }): void {
    logInfo(opts.customTagManagerId);
    if(!this.hasBeenInitialized ||
      isNullOrUndefinedOrEmpty(opts.customTagManagerId) ||
      !GtmHelper.isValidTagManagerId(opts.customTagManagerId)
    ) return;

    try {
      let customTagManager = this.getCustomTagManager(opts.customTagManagerId);
      if(customTagManager == null) {
        const { elementId, dataLayerName } = GtmHelper.createGoogleTagManager({
          document: this.document,
          renderer: this.renderer,
          tagManagerId: opts.customTagManagerId,
          hasConsent: this.hasAcceptedCookies
        });

        customTagManager = { tagManagerId: opts.customTagManagerId, elementId, dataLayerName };
        this.initializedTagManager.push(customTagManager);
      }

      this.customTagManagerStack.push(customTagManager);
      logInfo(`Custom Google Tag Manager has been activated for {${opts.context}}.`);
    }
    catch(error) {
      logError(`Failed to activate custom Google Tag Manager for {${opts.context}}.`, error);
    }
  }

  deactivateCustomTagManager(opts: { context: string, customTagManagerId: string }): void {
    for(let i = this.customTagManagerStack.length - 1; i >= 0; i--) {
      if(this.customTagManagerStack[i].tagManagerId === opts.customTagManagerId) {
        this.destroyTagManager(opts.customTagManagerId);
        this.customTagManagerStack.splice(i, 1);
        logInfo(`Custom Google Tag Manager has been deactivated for {${opts.context}}.`);
        return;
      }
    }
  }

  trackPromoCode(args: PromoCodeEventArgs): void {
    this.track({
      analyticsEventName: GTM_PROMO_CODE_ENTERED,
      extraData: {
        promo_code: args.promoCode,
      },
    });
  }

  trackPageView(args: PageViewEventArgs): void {
    // skip, because there is no such thing as page_view event on google analytics
  }

  trackSignUp(args: SignUpEventArgs): void {
    // skip, because it needs a totally different logic:
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=11828619753363400125-EU&client_type=gtag#sign_up
  }

  trackSearch(args: SearchEventArgs): void {
    this.track({
      analyticsEventName: GTM_SEARCH,
      extraData: {
        search_term: args.query
      }
    });
  }

  trackExplore(args: ExploreEventArgs): void {
    this.track({
      analyticsEventName: GTM_EXPLORE,
      extraData: {
        route: args.id,
        via: args.via,
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      },
    });
  }

  trackContentView(args: ContentViewEventArgs): void {
    // skip, because this needs a bigger refactor to make it work
    // the actual standard event is called view_item (view_item_list / view_cart / view_promotion)
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=11828619753363400125-EU&client_type=gtag#view_item
  }

  trackAddToCart(args: CartEventArgs): void {
    this.track({
      analyticsEventName: GTM_ADD_TO_CART,
      ecommerce: {
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackInitiateCheckout(args: CartEventArgs): void {
    this.track({
      analyticsEventName: GTM_INITIATE_CHECKOUT,
      ecommerce: {
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackCompleteRegistration(args: CartEventArgs): void {
    this.track({
      analyticsEventName: GTM_COMPLETE_REGISTRATION,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackInitiatePurchase(args: CartEventArgs): void {
    this.track({
      analyticsEventName: GTM_INITIATE_PURCHASE,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackPurchaseComplete(args: CartEventArgs): void {
    this.track({
      analyticsEventName: GTM_PURCHASE,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackPurchaseFailed(args: CartEventArgs): void {
    this.track({
      analyticsEventName:GTM_PURCHASE_FAILED,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  trackPurchaseCanceled(args: CartEventArgs): void {
    this.track({
      analyticsEventName: GTM_PURCHASE_CANCELED,
      ecommerce: {
        transaction_id: args.deduplicationId,
        currency: fromEnumAsString(args.cartPrice.currency),
        value: args.cartPrice.convertToDouble,
        items: args.cart.items.map((item) => ({
          item_id: item.uniqueId,
          item_name: item.name.getAny(args.language) ?? item.uniqueId,
          price: item.unitPrice.convertToDouble,
          quantity: item.quantity,
        }))
      },
      extraData: {
        event_name: args.eventName,
        event_id: args.eventId,
        business_id: args.businessId,
        organization_id: args.organizationId,
      }
    });
  }

  private getCustomTagManager(tagManagerId: string): TagManagerEntry | null {
    for(let i = 0; i < this.initializedTagManager.length; i++) {
      if(this.initializedTagManager[i].tagManagerId == tagManagerId) {
        return this.initializedTagManager[i];
      }
    }

    return null;
  }

  private destroyTagManager(tagManagerId: string): boolean {
    for(let i = this.initializedTagManager.length - 1; i >= 0; i--) {
      if(this.initializedTagManager[i].tagManagerId == tagManagerId) {
        GtmHelper.destroyGoogleTagManager(this.document, this.initializedTagManager[i].elementId);
        this.initializedTagManager.splice(i, 1);
        return true;
      }
    }

    return false;
  }

  private track({
    analyticsEventName,
    ecommerce = undefined,
    extraData = undefined,
  }: {
    analyticsEventName: string,
    ecommerce?: object,
    extraData?: object,
  }): void {
    if(!this.hasBeenInitialized || !this.hasAcceptedCookies) return;

    try {
      if(this.customTagManagerStack.length > 0) {
        logInfo(`Tracking GTM event {${analyticsEventName}} with params: ${JSON.stringify(ecommerce, null, 2)}`);
        const tagManager = this.customTagManagerStack[this.customTagManagerStack.length - 1];
        GtmHelper.logEventToGtmTracker({
          analyticsEventName,
          dataLayerName: tagManager.dataLayerName,
          ecommerce: ecommerce,
          extraData: extraData
        });
      }
    }
    catch(error) {
      logError(`Failed to track GTM event {${analyticsEventName}} with params: ${JSON.stringify(ecommerce, null, 2)}`, error);
    }
  }

  private trackConsent(hasConsent: boolean): void {
    if(!this.hasBeenInitialized) return;

    try {
      if(hasConsent) {
        logInfo('Granting consent for GTM.');
      }
      else {
        logInfo('Denying consent for GTM.');
      }

      for(const tagManager of this.customTagManagerStack) {
        GtmHelper.grantUpdatedConsentToGtmTracker(tagManager.dataLayerName, hasConsent);
      }
    }
    catch(error) {
      logError('Failed to track GTM consent.', error);
    }
  }
}